import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import { McpConnectionForm } from '../components/mcpConnections/McpConnectionForm';
import { useCreateMcpConnection } from '../hooks/useMcpConnections';
import type { AssistantMissingConnection } from '@/types/assistant';

/**
 * What the Assistant sidebar hands this page when the Composer guide asked for
 * a connection the account does not have (task 962).
 *
 * IT ARRIVES IN ROUTER STATE, NOT IN THE URL. Nothing here is secret, but a URL
 * is bookmarked, pasted and logged, and the credential field sits on the same
 * form — keeping the hand-off off the address bar keeps the whole shape on one
 * side of that line. An arriving state is a suggestion: every field stays
 * editable and the credential starts empty, because the key is the one part of
 * a connection the model never sees and never supplies.
 */
interface ConnectionPrefillState {
  connectionPrefill?: AssistantMissingConnection;
  /** Where the conversation was, so the save can offer the way back. */
  returnTo?: string;
}

export default function NewMcpConnectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const createMcpConnection = useCreateMcpConnection();

  const { connectionPrefill, returnTo } = (location.state ?? {}) as ConnectionPrefillState;
  /**
   * The saved connection, when there is somewhere to go back to. Without a
   * return location the page behaves exactly as it always has and navigates
   * straight to the new connection; with one, the User came here from a
   * conversation that is waiting on this, so the way back is the thing to offer
   * rather than a page they did not ask for.
   */
  const [saved, setSaved] = useState<{ id: string; name: string } | null>(null);

  const handleSubmit = async (data: any) => {
    const result = await createMcpConnection.mutateAsync(data);
    if (returnTo) {
      setSaved({ id: result.id, name: result.name });
      return;
    }
    navigate(`/mcp-connections/${result.id}`);
  };

  return (
    <div>
      <PageHeader
        title="Create MCP Connection"
        description="Configure a new Model Context Protocol server connection"
      />
      {connectionPrefill && !saved && (
        <div
          data-testid="mcp-connection-prefill-notice"
          className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200"
        >
          Innovate filled in what it knows about {connectionPrefill.description}. Add the key and
          save, and it can carry on building.
        </div>
      )}
      {saved ? (
        <div
          data-testid="mcp-connection-saved"
          className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <p className="text-sm text-gray-900 dark:text-white">
            <span className="font-medium">{saved.name}</span> is connected.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <Link
              to={returnTo ?? '/'}
              data-testid="mcp-connection-back-to-conversation"
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Back to the conversation
            </Link>
            <Link
              to={`/mcp-connections/${saved.id}`}
              className="text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              View the connection
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <McpConnectionForm
            mode="create"
            onSubmit={handleSubmit}
            initialData={connectionPrefill}
            focusCredential={Boolean(connectionPrefill)}
          />
        </div>
      )}
    </div>
  );
}
